import "@arco-design/web-react/es/Message/style";
import _Message from "@arco-design/web-react/es/Message";
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_LANG } from '@/locale';
import { getAccessToken, setAccessToken, setRefreshToken, removeAccessToken, removeRefreshToken, getRefreshToken } from './token';

// 创建axios实例
const request = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
  timeout: 10000 // 请求超时时间
});

/** 递归 trim 对象中所有字符串值的前后空格 */
function deepTrimStrings(obj) {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj === 'string') return obj.trim();
  if (Array.isArray(obj)) return obj.map(deepTrimStrings);
  if (typeof obj === 'object' && obj.constructor === Object) {
    const result = {};
    for (const key of Object.keys(obj)) {
      result[key] = deepTrimStrings(obj[key]);
    }
    return result;
  }
  return obj;
}

// 请求拦截器
request.interceptors.request.use(config => {
  // 对请求参数中的字符串值自动 trim 前后空格
  if (config.data && typeof config.data === 'object') {
    config.data = deepTrimStrings(config.data);
  }
  if (config.params && typeof config.params === 'object') {
    config.params = deepTrimStrings(config.params);
  }

  // 从cookie获取token
  const token = getAccessToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  // 生成requestID
  config.headers['X-Request-ID'] = uuidv4();

  // 获取当前语言
  const language = localStorage.getItem('language') || DEFAULT_LANG;
  config.headers['X-Language'] = language;
  return config;
}, error => {
  return Promise.reject(error);
});

// 响应拦截器
// 全局锁 & 等待队列
let isRefreshing = false;
let waitQueue = [];
let isRedirecting = false;

/** 登出：清空 token、拒绝队列、跳转登录 */
function forceLogout() {
  removeAccessToken();
  removeRefreshToken();
  waitQueue.forEach(item => item.reject(new Error('auth expired')));
  waitQueue = [];
  isRefreshing = false;
  if (!isRedirecting) {
    isRedirecting = true;
    window.location.href = '/login';
  }
}
request.interceptors.response.use(res => res, async error => {
  const status = error.response?.status;
  // 调用方显式传 { silent: true } 时，跳过全局错误弹窗，交由调用方自行处理
  // （用于把业务 404 等“预期内空结果”当作空态呈现，避免误报为服务错误）
  const silent = !!error.config?.silent;

  // 网络错误 / 超时（无响应）→ 提示错误，不跳转
  if (!error.response) {
    if (!silent) _Message.error(error.message || 'Network error');
    return Promise.reject(error);
  }

  // 非401：读取 body 错误信息，反馈给用户
  if (status !== 401) {
    const errMsg = error.response.data?.message || error.message;
    if (!silent) {
      if (status >= 500) {
        // 服务器错误 → 提示错误信息，不自动跳转
        _Message.error(errMsg || 'Server error');
      } else {
        // 4xx 客户端错误（400 参数错误、403 权限等）→ 提示具体错误
        _Message.error(errMsg);
      }
    }
    return Promise.reject(error);
  }
  const originalReq = error.config;

  // refresh请求本身返回401，说明refresh_token也失效，直接登出
  if (originalReq?._isRefreshRequest) {
    forceLogout();
    return Promise.reject(error);
  }

  // 已重试过的请求仍401，直接登出
  if (originalReq?._isRetry) {
    forceLogout();
    return Promise.reject(error);
  }

  // 场景1：正在刷新，把当前请求丢进队列
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      waitQueue.push({
        resolve,
        reject,
        config: originalReq
      });
    });
  }

  // 场景2：未刷新，开启刷新流程
  isRefreshing = true;
  const oldRefreshToken = getRefreshToken();
  let refreshRes;
  try {
    // 调用刷新接口，标记为refresh请求，避免被拦截器递归处理
    refreshRes = await request.post('/v1/user/refresh_token', {
      refresh_token: oldRefreshToken
    }, {
      _isRefreshRequest: true,
      timeout: 15000
    });
  } catch (refreshErr) {
    // 刷新请求失败：区分临时错误和 token 失效
    const refreshStatus = refreshErr.response?.status;
    if (refreshStatus === 401) {
      // refresh token 本身失效 → 确定需要登出
      forceLogout();
    } else {
      // 临时错误（网络超时、500 等）→ 仅拒绝队列中的请求，保留登录态
      waitQueue.forEach(item => item.reject(refreshErr));
      waitQueue = [];
      isRefreshing = false;
    }
    return Promise.reject(refreshErr);
  }

  // 刷新请求成功：检查业务码
  const {
    code,
    data
  } = refreshRes.data;
  if (code === 200) {
    // 更新本地token
    setAccessToken(data.access_token);
    setRefreshToken(data.refresh_token);
    const newToken = data.access_token;

    // 更新原始请求头，标记为重试请求后重发
    originalReq.headers.Authorization = `Bearer ${newToken}`;
    originalReq._isRetry = true;
    const newRes = await request(originalReq);

    // 把队列里所有等待的请求全部重发
    waitQueue.forEach(item => {
      item.config.headers.Authorization = `Bearer ${newToken}`;
      item.config._isRetry = true;
      item.resolve(request(item.config));
    });
    waitQueue = [];
    isRefreshing = false;
    return newRes;
  } else {
    // 业务码失败 = refresh token 确定失效 → 登出
    forceLogout();
    return Promise.reject(new Error('refresh token invalid'));
  }
});
export default request;